/**
 * Terrain Manager - Manages terrain generation and rendering
 * 
 * Supports:
 * - Heightmap-based terrain (PNG images)
 * - Procedural generation
 * - Physics integration with Havok
 * - Chunk-based loading (future)
 * 
 * Reference: https://doc.babylonjs.com/features/featuresDeepDive/mesh/creation/set/height_map
 */

import { Scene } from "@babylonjs/core/scene";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { GroundMesh } from "@babylonjs/core/Meshes/groundMesh";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Texture } from "@babylonjs/core/Materials/Textures/texture";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { VertexData } from "@babylonjs/core/Meshes/mesh.vertexData";
import { FloatArray } from "@babylonjs/core/types";
import { Ray } from "@babylonjs/core/Culling/ray";
import { PhysicsAggregate } from "@babylonjs/core/Physics/v2/physicsAggregate";
import { PhysicsShapeType } from "@babylonjs/core/Physics/v2/IPhysicsEnginePlugin";
import { IDisposable } from "../core/Engine.types";
import { TerrainConfig, TerrainLayer, HeightmapOptions, FlatSpawnArea } from "./Terrain.types";

export class TerrainManager implements IDisposable {
    private _scene: Scene;
    private _terrain: GroundMesh | Mesh | null = null;
    private _material: StandardMaterial | null = null;
    private _config: TerrainConfig;
    private _isReady: boolean = false;
    private _onReadyCallbacks: Array<(terrain: GroundMesh | Mesh) => void> = [];
    private _physicsAggregate: PhysicsAggregate | null = null;
    private _flatAreas: FlatSpawnArea[] = [];

    public get mesh(): GroundMesh | Mesh | null {
        return this._terrain;
    }

    public get isReady(): boolean {
        return this._isReady;
    }

    public get config(): TerrainConfig {
        return { ...this._config };
    }

    public get physicsAggregate(): PhysicsAggregate | null {
        return this._physicsAggregate;
    }

    /** Get terrain bounds */
    public get bounds(): { minX: number; maxX: number; minZ: number; maxZ: number } {
        const halfWidth = this._config.width / 2;
        const halfDepth = this._config.depth / 2;
        return {
            minX: -halfWidth,
            maxX: halfWidth,
            minZ: -halfDepth,
            maxZ: halfDepth,
        };
    }

    /** Get flat spawn areas */
    public get flatAreas(): FlatSpawnArea[] {
        return [...this._flatAreas];
    }

    constructor(scene: Scene, config?: Partial<TerrainConfig>) {
        this._scene = scene;
        this._config = {
            width: config?.width ?? 100,
            depth: config?.depth ?? 100,
            subdivisions: config?.subdivisions ?? 100,
            minHeight: config?.minHeight ?? 0,
            maxHeight: config?.maxHeight ?? 10,
            ...config,
        };
    }

    /**
     * Register a callback to be called when terrain is ready
     */
    public onReady(callback: (terrain: GroundMesh | Mesh) => void): void {
        if (this._isReady && this._terrain) {
            callback(this._terrain);
        } else {
            this._onReadyCallbacks.push(callback);
        }
    }

    /**
     * Create terrain from a heightmap PNG image
     * 
     * @param heightmapUrl - URL to the heightmap PNG (grayscale recommended)
     * @param options - Additional options for heightmap loading
     * @returns Promise that resolves when terrain is ready
     * 
     * Heightmap format:
     * - White (255) = maximum height
     * - Black (0) = minimum height
     * - Grayscale values = interpolated heights
     */
    public async createFromHeightmap(
        heightmapUrl: string,
        options?: HeightmapOptions
    ): Promise<GroundMesh> {
        const { width, depth, subdivisions, minHeight, maxHeight } = this._config;
        const textureUrl = options?.textureUrl;
        const textureScale = options?.textureScale ?? 10;

        return new Promise((resolve, reject) => {
            try {
                this._terrain = MeshBuilder.CreateGroundFromHeightMap(
                    "terrain",
                    heightmapUrl,
                    {
                        width,
                        height: depth,
                        subdivisions,
                        minHeight,
                        maxHeight,
                        updatable: options?.updatable ?? false,
                        onReady: (mesh) => {
                            mesh.computeWorldMatrix(true);
                            
                            // Apply material
                            this._material = this._createTerrainMaterial(textureUrl, textureScale);
                            mesh.material = this._material;
                            
                            // Mark as ready
                            this._isReady = true;
                            
                            // Call ready callbacks
                            for (const callback of this._onReadyCallbacks) {
                                callback(mesh);
                            }
                            this._onReadyCallbacks = [];
                            
                            console.log(`TerrainManager: Heightmap loaded from ${heightmapUrl}`);
                            resolve(mesh as GroundMesh);
                        },
                    },
                    this._scene
                );
            } catch (error) {
                reject(error);
            }
        });
    }

    /**
     * Create procedurally generated terrain
     * @param seed - Random seed for generation
     * @param flatAreas - Optional array of flat spawn areas
     */
    public createProcedural(seed?: number, flatAreas?: FlatSpawnArea[]): Mesh {
        const { width, depth, subdivisions, minHeight, maxHeight } = this._config;
        const actualSeed = seed ?? Math.random() * 10000;

        // Store flat areas for later reference
        this._flatAreas = flatAreas || [];

        // Create base ground mesh
        this._terrain = MeshBuilder.CreateGround(
            "terrain",
            {
                width,
                height: depth,
                subdivisions,
                updatable: true,
            },
            this._scene
        ) as GroundMesh;

        // Generate heightmap using simplex-like noise
        const positions = this._terrain.getVerticesData("position");
        if (positions) {
            const newPositions = this._applyProceduralHeight(
                positions,
                actualSeed,
                minHeight,
                maxHeight,
                this._flatAreas
            );
            this._terrain.updateVerticesData("position", newPositions);
            this._terrain.computeWorldMatrix(true);
            
            // Recompute normals for proper lighting
            const normals: number[] = [];
            VertexData.ComputeNormals(
                newPositions,
                this._terrain.getIndices()!,
                normals
            );
            this._terrain.updateVerticesData("normal", normals);
        }

        // Apply material
        this._material = this._createTerrainMaterial();
        this._terrain.material = this._material;
        
        // Mark as ready
        this._isReady = true;
        for (const callback of this._onReadyCallbacks) {
            callback(this._terrain);
        }
        this._onReadyCallbacks = [];

        // Log flat areas
        if (this._flatAreas.length > 0) {
            console.log(`TerrainManager: Created ${this._flatAreas.length} flat spawn areas:`);
            for (const area of this._flatAreas) {
                console.log(`  - "${area.name}" at (${area.centerX}, ${area.centerZ}) radius=${area.radius}`);
            }
        }

        return this._terrain;
    }

    /**
     * Create a simple flat terrain
     */
    public createFlat(textureUrl?: string): GroundMesh {
        const { width, depth, subdivisions } = this._config;

        this._terrain = MeshBuilder.CreateGround(
            "terrain",
            {
                width,
                height: depth,
                subdivisions,
            },
            this._scene
        ) as GroundMesh;

        this._material = this._createTerrainMaterial(textureUrl);
        this._terrain.material = this._material;
        
        // Mark as ready
        this._isReady = true;
        for (const callback of this._onReadyCallbacks) {
            callback(this._terrain);
        }
        this._onReadyCallbacks = [];

        return this._terrain as GroundMesh;
    }

    /**
     * Setup physics collision for the terrain
     * Call this after terrain is created and physics is initialized
     */
    public setupPhysics(): PhysicsAggregate | null {
        if (!this._terrain) {
            console.warn("TerrainManager: Cannot setup physics - no terrain created");
            return null;
        }

        if (this._physicsAggregate) {
            console.warn("TerrainManager: Physics already setup");
            return this._physicsAggregate;
        }

        // Create static physics body for terrain
        // Use MESH shape for accurate collision with heightmap
        this._physicsAggregate = new PhysicsAggregate(
            this._terrain,
            PhysicsShapeType.MESH,
            {
                mass: 0, // Static body
                friction: 0.8,
                restitution: 0.1,
            },
            this._scene
        );

        console.log("TerrainManager: Physics collision setup complete");
        return this._physicsAggregate;
    }

    /**
     * Get height at world position using multiple methods
     */
    public getHeightAtPosition(x: number, z: number): number {
        if (!this._terrain) return 0;

        // Check bounds first
        const bounds = this.bounds;
        if (x < bounds.minX || x > bounds.maxX || z < bounds.minZ || z > bounds.maxZ) {
            return 0;
        }

        // For GroundMesh, use built-in method (most accurate)
        if (this._terrain instanceof GroundMesh) {
            const height = this._terrain.getHeightAtCoordinates(x, z);
            // getHeightAtCoordinates can return NaN outside bounds
            if (!isNaN(height)) {
                return height;
            }
        }

        // Fallback: use raycast for any mesh type
        return this._raycastHeight(x, z);
    }

    /**
     * Raycast to find terrain height at x, z
     */
    private _raycastHeight(x: number, z: number): number {
        if (!this._terrain) return 0;

        const rayStart = new Vector3(x, this._config.maxHeight + 50, z);
        const rayDirection = Vector3.Down();
        const rayLength = this._config.maxHeight + 100;

        const ray = new Ray(rayStart, rayDirection, rayLength);
        const hit = ray.intersectsMesh(this._terrain, false);

        if (hit.hit && hit.pickedPoint) {
            return hit.pickedPoint.y;
        }

        return 0;
    }

    /**
     * Check if a world position is within terrain bounds
     */
    public isInsideBounds(x: number, z: number): boolean {
        const bounds = this.bounds;
        return x >= bounds.minX && x <= bounds.maxX && 
               z >= bounds.minZ && z <= bounds.maxZ;
    }

    /**
     * Get normal at world position
     */
    public getNormalAtPosition(x: number, z: number): Vector3 {
        if (!this._terrain || !(this._terrain instanceof GroundMesh)) {
            return Vector3.Up();
        }
        return this._terrain.getNormalAtCoordinates(x, z);
    }

    /**
     * Set terrain texture layers (for multi-texture terrain)
     */
    public setTextureLayers(_layers: TerrainLayer[]): void {
        // TODO: Implement multi-texture terrain with shader
        console.warn("Multi-texture terrain not yet implemented");
    }

    /**
     * Set a single texture on the terrain
     * @param textureUrl - URL to the texture image
     * @param tileScale - How many times to tile the texture (default: 10)
     */
    public setTexture(textureUrl: string, tileScale: number = 10): void {
        if (!this._terrain) {
            console.warn("TerrainManager: Cannot set texture - terrain not created yet");
            return;
        }
        
        // Dispose old material if it exists
        if (this._material) {
            this._material.dispose();
        }
        
        this._material = this._createTerrainMaterial(textureUrl, tileScale);
        this._terrain.material = this._material;
        console.log(`TerrainManager: Applied texture "${textureUrl}" with tileScale=${tileScale}`);
    }

    /**
     * Dispose terrain resources
     */
    public dispose(): void {
        if (this._physicsAggregate) {
            this._physicsAggregate.dispose();
            this._physicsAggregate = null;
        }
        if (this._material) {
            this._material.dispose();
            this._material = null;
        }
        if (this._terrain) {
            this._terrain.dispose();
            this._terrain = null;
        }
    }

    /**
     * Create terrain material
     */
    private _createTerrainMaterial(textureUrl?: string, textureScale: number = 10): StandardMaterial {
        const material = new StandardMaterial("terrainMaterial", this._scene);
        
        if (textureUrl) {
            material.diffuseTexture = new Texture(textureUrl, this._scene);
            (material.diffuseTexture as Texture).uScale = textureScale;
            (material.diffuseTexture as Texture).vScale = textureScale;
        } else {
            // Default grass-like color
            material.diffuseColor = new Color3(0.3, 0.5, 0.2);
        }

        material.specularColor = new Color3(0.1, 0.1, 0.1);
        material.specularPower = 8;

        return material;
    }

    /**
     * Apply procedural heightmap to vertex positions with flat spawn areas
     */
    private _applyProceduralHeight(
        positions: FloatArray,
        seed: number,
        minHeight: number,
        maxHeight: number,
        flatAreas: FlatSpawnArea[] = []
    ): Float32Array {
        const newPositions = new Float32Array(positions.length);
        const heightRange = maxHeight - minHeight;

        // First pass: calculate base heights and determine flat area target heights
        const baseHeights: number[] = [];
        for (let i = 0; i < positions.length; i += 3) {
            const x = positions[i];
            const z = positions[i + 2];
            const height = this._generateNoise(x, z, seed) * heightRange + minHeight;
            baseHeights.push(height);
        }

        // Calculate target heights for flat areas (average of surrounding area)
        const flatAreaTargets = new Map<string, number>();
        for (const area of flatAreas) {
            if (area.targetHeight !== undefined) {
                flatAreaTargets.set(area.name, area.targetHeight);
            } else {
                // Calculate average height in the area
                let sum = 0;
                let count = 0;
                for (let i = 0; i < positions.length; i += 3) {
                    const x = positions[i];
                    const z = positions[i + 2];
                    const dist = this._getDistanceToFlatArea(x, z, area);
                    if (dist <= 0) { // Inside the flat area
                        sum += baseHeights[i / 3];
                        count++;
                    }
                }
                const avgHeight = count > 0 ? sum / count : (minHeight + maxHeight) / 2;
                flatAreaTargets.set(area.name, avgHeight);
            }
        }

        // Second pass: apply heights with flat areas
        for (let i = 0; i < positions.length; i += 3) {
            const x = positions[i];
            const z = positions[i + 2];
            
            let height = baseHeights[i / 3];

            // Check each flat area and blend
            for (const area of flatAreas) {
                const dist = this._getDistanceToFlatArea(x, z, area);
                const blendDist = area.blendDistance ?? 5;
                const targetHeight = flatAreaTargets.get(area.name) ?? height;

                if (dist <= 0) {
                    // Inside flat area - use target height
                    height = targetHeight;
                } else if (dist < blendDist) {
                    // In blend zone - smooth transition
                    const t = dist / blendDist;
                    const smoothT = this._smoothstep(t);
                    height = targetHeight * (1 - smoothT) + height * smoothT;
                }
            }

            newPositions[i] = x;
            newPositions[i + 1] = height;
            newPositions[i + 2] = z;
        }

        return newPositions;
    }

    /**
     * Get distance from point to edge of flat area (negative = inside)
     */
    private _getDistanceToFlatArea(x: number, z: number, area: FlatSpawnArea): number {
        if (area.shape === "square") {
            // Square distance
            const dx = Math.abs(x - area.centerX) - area.radius;
            const dz = Math.abs(z - area.centerZ) - area.radius;
            return Math.max(dx, dz);
        } else {
            // Circle distance (default)
            const dx = x - area.centerX;
            const dz = z - area.centerZ;
            return Math.sqrt(dx * dx + dz * dz) - area.radius;
        }
    }

    /**
     * Smoothstep interpolation
     */
    private _smoothstep(t: number): number {
        return t * t * (3 - 2 * t);
    }

    /**
     * Check if a position is inside a flat spawn area
     */
    public isInFlatArea(x: number, z: number): FlatSpawnArea | null {
        for (const area of this._flatAreas) {
            const dist = this._getDistanceToFlatArea(x, z, area);
            if (dist <= 0) {
                return area;
            }
        }
        return null;
    }

    /**
     * Get flat spawn area by name
     */
    public getFlatArea(name: string): FlatSpawnArea | undefined {
        return this._flatAreas.find(a => a.name === name);
    }

    /**
     * Get a random position within a flat spawn area
     */
    public getRandomPositionInFlatArea(areaName: string): Vector3 | null {
        const area = this.getFlatArea(areaName);
        if (!area) return null;

        const angle = Math.random() * Math.PI * 2;
        const distance = Math.random() * area.radius * 0.8; // Stay away from edges
        
        const x = area.centerX + Math.cos(angle) * distance;
        const z = area.centerZ + Math.sin(angle) * distance;
        const y = this.getHeightAtPosition(x, z);

        return new Vector3(x, y, z);
    }

    /**
     * Add a flat area dynamically (requires terrain regeneration)
     */
    public addFlatArea(area: FlatSpawnArea): void {
        this._flatAreas.push(area);
    }

    /**
     * Simple noise function (can be replaced with better noise library)
     * Enhanced with multiple layers for more realistic terrain
     */
    private _generateNoise(x: number, z: number, seed: number): number {
        // Layer 1: Large rolling hills (low frequency, high amplitude) - MORE DRAMATIC
        const hills = this._fbmNoise(x * 0.012, z * 0.012, seed, 4, 0.55, 2.2) * 0.55;
        
        // Layer 2: Medium bumps and features - MORE PRONOUNCED
        const bumps = this._fbmNoise(x * 0.04, z * 0.04, seed + 100, 5, 0.5, 2.0) * 0.35;
        
        // Layer 3: Small detail noise - ROUGHER SURFACE
        const detail = this._fbmNoise(x * 0.15, z * 0.15, seed + 200, 3, 0.5, 2.0) * 0.12;
        
        // Layer 4: Ridged noise for occasional ridges/valleys - STRONGER RIDGES
        const ridged = this._ridgedNoise(x * 0.02, z * 0.02, seed + 300) * 0.2;
        
        // Layer 5: Mountain peaks - creates occasional tall peaks
        const peakNoise = this._fbmNoise(x * 0.006, z * 0.006, seed + 600, 2, 0.6, 2.0);
        const peaks = Math.pow(Math.max(0, peakNoise - 0.3), 2) * 0.4;
        
        // Layer 6: Valley carving - creates winding valleys
        const valleyNoise = this._ridgedNoise(x * 0.008, z * 0.008, seed + 700);
        const valleys = Math.pow(1 - valleyNoise, 3) * -0.15; // Negative to carve down
        
        // Layer 7: Micro terrain roughness for natural ground feel
        const microRoughness = this._simpleNoise(x * 0.3 + seed + 800, z * 0.3 + seed + 800) * 0.03;
        
        // Layer 8: Plateau/flat area tendency (creates natural flat spots) - REDUCED
        const plateauNoise = this._simpleNoise(x * 0.02 + seed + 400, z * 0.02 + seed + 400);
        const plateauFactor = Math.pow(plateauNoise, 3); // Cubic makes plateaus rarer
        
        // Combine all layers
        let height = hills + bumps + detail + ridged + peaks + valleys + microRoughness;
        
        // Apply plateau effect - flatten areas where plateauFactor is high (but less often)
        if (plateauFactor > 0.7) {
            const flattenAmount = (plateauFactor - 0.7) / 0.3; // 0-1 range
            const localAvg = hills; // Use large-scale height as plateau level
            height = height * (1 - flattenAmount * 0.5) + localAvg * flattenAmount * 0.5;
        }
        
        // Add occasional dips (depressions) - DEEPER
        const dipNoise = this._simpleNoise(x * 0.025 + seed + 500, z * 0.025 + seed + 500);
        if (dipNoise > 0.82) {
            const dipStrength = (dipNoise - 0.82) / 0.18;
            height -= dipStrength * 0.3; // Create deeper depressions
        }
        
        // Add occasional steep slopes
        const slopeNoise = this._simpleNoise(x * 0.035 + seed + 900, z * 0.035 + seed + 900);
        if (slopeNoise > 0.75) {
            const slopeStrength = (slopeNoise - 0.75) / 0.25;
            height += slopeStrength * hills * 0.5; // Amplify hills in these areas
        }
        
        // Normalize to 0-1 range (accounting for all layers)
        // Total possible range is roughly -0.45 to +1.25 with all layers
        height = (height + 0.45) / 1.7; // Shift and scale for full range usage
        height = Math.max(0, Math.min(1, height)); // Clamp
        
        return height;
    }
    
    /**
     * Fractal Brownian Motion noise - layered noise for natural terrain
     */
    private _fbmNoise(x: number, z: number, seed: number, octaves: number, persistence: number, lacunarity: number): number {
        let total = 0;
        let frequency = 1;
        let amplitude = 1;
        let maxValue = 0;

        for (let i = 0; i < octaves; i++) {
            total += this._simpleNoise(x * frequency + seed, z * frequency + seed) * amplitude;
            maxValue += amplitude;
            amplitude *= persistence;
            frequency *= lacunarity;
        }

        return total / maxValue;
    }
    
    /**
     * Ridged noise - creates ridge-like features
     */
    private _ridgedNoise(x: number, z: number, seed: number): number {
        let total = 0;
        let frequency = 1;
        let amplitude = 1;
        const octaves = 3;
        
        for (let i = 0; i < octaves; i++) {
            // Get absolute value and invert to create ridges
            let n = this._simpleNoise(x * frequency + seed, z * frequency + seed);
            n = 1 - Math.abs(n * 2 - 1); // Creates sharp ridges
            n = n * n; // Square for sharper ridges
            total += n * amplitude;
            amplitude *= 0.5;
            frequency *= 2;
        }
        
        return total / 1.75; // Normalize
    }

    /**
     * Basic noise function (pseudo-random based on position)
     */
    private _simpleNoise(x: number, y: number): number {
        // Smooth interpolation
        const ix = Math.floor(x);
        const iy = Math.floor(y);
        const fx = x - ix;
        const fy = y - iy;

        const a = this._hash(ix, iy);
        const b = this._hash(ix + 1, iy);
        const c = this._hash(ix, iy + 1);
        const d = this._hash(ix + 1, iy + 1);

        const ux = fx * fx * (3 - 2 * fx);
        const uy = fy * fy * (3 - 2 * fy);

        return a + (b - a) * ux + (c - a) * uy + (a - b - c + d) * ux * uy;
    }

    /**
     * Hash function for noise
     */
    private _hash(x: number, y: number): number {
        const n = Math.sin(x * 127.1 + y * 311.7) * 43758.5453;
        return n - Math.floor(n);
    }
}
